import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface CampaignState {
  id: string;
  created_by: string;
  type: [string, string];
  name: { [key: string]: string };
  description: { [key: string]: string };
  document: string;
  background_image: string | null;
  display_data: any;
  background_images: string[];
  dynamic_data: boolean;
  rooms: string[];
  schedule: [string, string];
  schedule_detail: string;
  next_occurrence: {
    value: string;
    string: string;
    timestamp: number;
  };
}

export interface PhotoAlbumState {
  id: string;
  created_by: string;
  last_updated_by: string;
  datetime_created: string;
  datetime_updated: string | null;
  name: { [key: string]: string };
  rooms: string[];
  photos: Array<{
    id: string;
    uploaded_by: string;
    last_updated_by: string;
    datetime_uploaded: string;
    datetime_updated: string | null;
    url: string;
    description: string | null;
  }>;
  status: [string, string];
  size: number;
}

export interface EventState {
  id: string;
  created_by: string;
  last_updated_by: string;
  name: { [key: string]: string };
  target: [string, string];
  type: [string, string];
  rooms: string[];
  schedule: [string, string];
  datetime_start: string | null;
  datetime_end: string | null;
  schedule_type: [string, string] | null;
  schedule_detail: any;
  background_image: string | null;
  link: string | null;
  qr_code: string | null;
  status: [string, string];
  next_occurrence: {
    value: string;
    string: string;
    timestamp: number;
  };
}

export interface ServiceState {
  id: string;
  created_by: string;
  last_updated_by: string;
  name: { [key: string]: string };
  category: [string, string];
  description: { [key: string]: string };
  directions: { [key: string]: string };
  availability: [string, string];
  availability_detail: {
    ALL: boolean;
    STD: null | {
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    };
    STW: null | Array<{
      day_of_week: number[];
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
    STM: null | Array<{
      day_of_month: number[];
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
    SLD: Array<{
      date: string;
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
  };
  status: [string, string];
  next_occurrence: {
    value: string;
    string: string;
    timestamp: number;
  };
}

export interface MenuState {
  id: string;
  created_by: string;
  last_updated_by: string;
  name: { [key: string]: string };
  meal_time: [string, string];
  items: { [key: string]: string };
  schedule: [string, string];
  next_occurrence: {
    value: string;
    string: string;
    timestamp: number;
  };
  recurring_schedule_option: [string, string] | null;
  schedule_detail: {
    O: null | {
      start_date: string;
      end_date: string;
    };
    R: {
      STD: null | {
        start_time: { hour: number; minute: number };
        end_time: { hour: number; minute: number };
      };
      STW: null | Array<{
        day_of_week: number[];
        start_time: { hour: number; minute: number };
        end_time: { hour: number; minute: number };
      }>;
      STM: null | Array<{
        day_of_month: number[];
        start_time: { hour: number; minute: number };
        end_time: { hour: number; minute: number };
      }>;
      SLD: string[];
    };
  };
  status: [string, string];
}

export interface NewsletterState {
  id: string;
  created_by: string;
  last_updated_by: string;
  name: { [key: string]: string };
  status: [string, string];
  sections: Array<{
    id: string;
    pos: number;
    datetime_created: string;
    datetime_updated: string | null;
    header: { [key: string]: string };
    body: { [key: string]: string };
    image: null | { id: string; source: string; data: string };
  }>;
}

export interface WeblinkState {
  id: string;
  created_by: string;
  last_updated_by: string;
  datetime_created: string;
  datetime_updated: string | null;
  name: { [key: string]: string };
  description: { [key: string]: string };
  type: [string, string];
  image: string | null;
  url: string;
  qr_code: string | null;
  rooms: string[];
  status: [string, string];
}

export interface TriviaState {
  id: string;
  created_by: string;
  last_updated_by: string;
  datetime_created: string;
  datetime_updated: string | null;
  response: { [key: string]: string };
}

// Define a type for the slice state
export interface ContentState {
  campaigns: {
    data: CampaignState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  photo_albums: {
    data: PhotoAlbumState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  events: {
    data: EventState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  services: {
    data: ServiceState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  menus: {
    data: MenuState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
    pagination: {
      has_previous: false;
      has_next: false;
      current_page: 1;
      total_pages: 1;
      total_objects: 0;
    };
  };
  newsletters: {
    data: NewsletterState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  weblinks: {
    data: WeblinkState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  trivia: {
    data: TriviaState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: ContentState = {
  campaigns: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  photo_albums: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  events: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  services: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  menus: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0,
    },
  },
  newsletters: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  weblinks: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  trivia: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get campaigns
export const getCampaigns = createAsyncThunk(
  "content/campaigns/get",
  async (
    obj: {
      type: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/campaign/`;
      if (obj.type) url += `?type=${obj.type}`;
      if (obj.dateRange)
        url += `${obj.type ? "&" : "?"}dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create pinned campaign
export const createPinnedCampaign = createAsyncThunk(
  "content/campaigns/create/pinned/post",
  async (
    obj: {
      name: { [key: string]: string };
      description: { [key: string]: string };
      property: string;
      backgroundImage: string;
      title: { [key: string]: string };
      body: { [key: string]: string };
      rooms: string[];
      schedule: string;
      scheduleDetail: {
        O: null | {
          start_date: string;
          end_date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        R: {
          STD: null | {
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STW: null | {
            day_of_week: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STM: null | {
            day_of_month: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
        };
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        description: obj.description,
        property: obj.property,
        background_image: obj.backgroundImage,
        title: obj.title,
        body: obj.body,
        rooms: obj.rooms,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/campaign/pinned/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create rotating campaign
export const createRotatingCampaign = createAsyncThunk(
  "content/campaigns/create/rotating/post",
  async (
    obj: {
      name: { [key: string]: string };
      description: { [key: string]: string };
      property: string;
      backgroundImage: string;
      headerText: { [key: string]: string };
      primaryText: { [key: string]: string };
      secondaryText: { [key: string]: string };
      rooms: string[];
      schedule: string;
      scheduleDetail: {
        O: null | {
          start_date: string;
          end_date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        R: {
          STD: null | {
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STW: null | {
            day_of_week: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STM: null | {
            day_of_month: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
        };
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        description: obj.description,
        property: obj.property,
        background_image: obj.backgroundImage,
        header_text: obj.headerText,
        primary_text: obj.primaryText,
        secondary_text: obj.secondaryText,
        rooms: obj.rooms,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/campaign/rotating/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create template campaign
export const createTemplateCampaign = createAsyncThunk(
  "content/campaigns/create/template/post",
  async (
    obj: {
      name: { [key: string]: string };
      description: { [key: string]: string };
      property: string;
      templateType: string;
      displayType: string;
      dynamicData: boolean;
      backgroundImageUrlArray: string[];
      rooms: string[];
      schedule: string;
      scheduleDetail: {
        O: null | {
          start_date: string;
          end_date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        R: {
          STD: null | {
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STW: null | {
            day_of_week: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STM: null | {
            day_of_month: number[];
            start_date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
        };
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        description: obj.description,
        property: obj.property,
        type: obj.templateType,
        campaign_type: obj.displayType,
        dynamic_data: obj.dynamicData,
        document_data: [],
        background_images: obj.backgroundImageUrlArray,
        rooms: obj.rooms,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/campaign/template/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete campaign
export const deleteCampaigns = createAsyncThunk(
  "content/campaigns/delete",
  async (
    obj: {
      campaignIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.campaignIds },
      };

      const res = await axios.delete("/api/campaign/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get photo albums
export const getPhotoAlbums = createAsyncThunk(
  "content/photo-albums/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/photos/album/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create photo album
export const createPhotoAlbum = createAsyncThunk(
  "content/photo-albums/post",
  async (
    obj: {
      name: { [key: string]: string };
      property: string;
      rooms: string[];
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        rooms: obj.rooms,
        status: obj.status,
      };

      // Build URL
      const url = "/api/photos/album/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update photo album
export const updatePhotoAlbum = createAsyncThunk(
  "content/photo-albums/put",
  async (
    obj: {
      albumId: string;
      name: { [key: string]: string };
      property: string;
      photos: Array<{ id: any; source: string; data: any }>;
      rooms: string[];
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.albumId,
        name: obj.name,
        property: obj.property,
        photos: obj.photos,
        rooms: obj.rooms,
        status: obj.status,
      };
      const res = await axios.put("/api/photos/album/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete photo albums
export const deletePhotoAlbums = createAsyncThunk(
  "content/photo-albums/delete",
  async (
    obj: {
      albumIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.albumIds },
      };

      const res = await axios.delete("/api/photos/album/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get events
export const getEvents = createAsyncThunk(
  "content/events/get",
  async (
    obj: {
      type: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/event/`;
      if (obj.type) url += `?type=${obj.type}`;
      if (obj.dateRange)
        url += `${obj.type === null ? "?" : "&"}dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create event
export const createEvent = createAsyncThunk(
  "content/events/post",
  async (
    obj: {
      name: { [key: string]: string };
      type: string;
      property: string;
      schedule: string;
      scheduleDetail: {
        O: null | {
          start_date: string;
          end_date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        R: {
          STD: null | {
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STW: null | Array<{
            day_of_week: number[];
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
          STM: null | Array<{
            day_of_month: number[];
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
          SLD: Array<{
            date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
        };
      };
      target: string;
      rooms: string[];
      backgroundImage: { id: any; source: string; data: any } | null;
      link: string | null;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        type: obj.type,
        property: obj.property,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        target: obj.target,
        rooms: obj.rooms,
        background_image: obj.backgroundImage,
        link: obj.link,
        status: obj.status,
      };

      // Build URL
      const url = "/api/event/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update event
export const updateEvent = createAsyncThunk(
  "content/events/put",
  async (
    obj: {
      eventId: string;
      name: { [key: string]: string };
      type: string;
      property: string;
      schedule: string;
      scheduleDetail: {
        O: null | {
          start_date: string;
          end_date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        R: {
          STD: null | {
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          };
          STW: null | Array<{
            day_of_week: number[];
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
          STM: null | Array<{
            day_of_month: number[];
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
          SLD: Array<{
            date: string;
            start_time: { hour: number; minute: number };
            end_time: { hour: number; minute: number };
          }>;
        };
      };
      target: string;
      rooms: string[];
      backgroundImage: { id: any; source: string; data: any } | null;
      link: string | null;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.eventId,
        name: obj.name,
        type: obj.type,
        property: obj.property,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        target: obj.target,
        rooms: obj.rooms,
        background_image: obj.backgroundImage,
        link: obj.link,
        status: obj.status,
      };

      // Build URL
      const url = "/api/event/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete events
export const deleteEvents = createAsyncThunk(
  "content/events/delete",
  async (
    obj: {
      eventIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.eventIds },
      };

      const res = await axios.delete("/api/event/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get services
export const getServices = createAsyncThunk(
  "content/services/get",
  async (
    obj: {
      category: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/services/`;
      if (obj.category) url += `?category=${obj.category}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create service
export const createService = createAsyncThunk(
  "content/services/post",
  async (
    obj: {
      name: { [key: string]: string };
      property: string;
      category: string;
      description: { [key: string]: string };
      directions: { [key: string]: string };
      availability: string;
      availabilityDetail: {
        ALL: boolean;
        STD: null | {
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        STW: null | Array<{
          day_of_week: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        STM: null | Array<{
          day_of_month: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        SLD: Array<{
          date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        category: obj.category,
        description: obj.description,
        directions: obj.directions,
        availability: obj.availability,
        availability_detail: obj.availabilityDetail,
        status: obj.status,
      };
      console.log(body.name);

      // Build URL
      const url = "/api/services/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update service
export const updateService = createAsyncThunk(
  "content/services/put",
  async (
    obj: {
      serviceId: string;
      name: { [key: string]: string };
      property: string;
      category: string;
      description: { [key: string]: string };
      directions: { [key: string]: string };
      availability: string;
      availabilityDetail: any;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.serviceId,
        name: obj.name,
        property: obj.property,
        category: obj.category,
        description: obj.description,
        directions: obj.directions,
        availability: obj.availability,
        availability_detail: obj.availabilityDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/services/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete services
export const deleteServices = createAsyncThunk(
  "content/services/delete",
  async (
    obj: {
      serviceIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.serviceIds },
      };

      const res = await axios.delete("/api/services/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get menus
export const getMenus = createAsyncThunk(
  "content/menus/get",
  async (
    obj: {
      pageNum: number | boolean;
      mealTime: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/menu/?page=${obj.pageNum}`;
      if (obj.mealTime) url += `&mealTime=${obj.mealTime}`;
      if (obj.dateRange) url += `&dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create menus
export const createMenu = createAsyncThunk(
  "content/menus/post",
  async (
    obj: {
      name: { [key: string]: string };
      property: string;
      mealTime: string;
      items: { [key: string]: string };
      schedule: string;
      scheduleDetail: {
        STD: null | {
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        STW: null | Array<{
          day_of_week: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        STM: null | Array<{
          day_of_month: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        SLD: Array<{
          date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        meal_time: obj.mealTime,
        items: obj.items,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/menu/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update menu
export const updateMenu = createAsyncThunk(
  "content/menus/put",
  async (
    obj: {
      menuId: string;
      name: { [key: string]: string };
      property: string;
      mealTime: string;
      items: { [key: string]: string };
      schedule: string;
      scheduleDetail: {
        STD: null | {
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        };
        STW: null | Array<{
          day_of_week: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        STM: null | Array<{
          day_of_month: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        SLD: Array<{
          date: string;
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.menuId,
        name: obj.name,
        property: obj.property,
        meal_time: obj.mealTime,
        items: obj.items,
        schedule: obj.schedule,
        schedule_detail: obj.scheduleDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/menu/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete menus
export const deleteMenus = createAsyncThunk(
  "content/menus/delete",
  async (
    obj: {
      menuIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.menuIds },
      };

      const res = await axios.delete("/api/menu/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get newsletters
export const getNewsletters = createAsyncThunk(
  "content/newsletters/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/newsletters/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create newsletter
export const createNewsletter = createAsyncThunk(
  "content/newsletter/post",
  async (
    obj: {
      name: { [key: string]: string };
      property: string;
      status: string;
      sections: Array<{
        id: string;
        pos: number;
        header: { [key: string]: string };
        body: { [key: string]: string };
        image: null | { source: string; data: string };
      }>;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        status: obj.status,
        sections: obj.sections,
      };

      // Build URL
      const url = "/api/newsletters/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update newsletter
export const updateNewsletter = createAsyncThunk(
  "content/newsletter/put",
  async (
    obj: {
      newsletterId: string;
      name: { [key: string]: string };
      property: string;
      status: string;
      sections: Array<any>;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.newsletterId,
        name: obj.name,
        property: obj.property,
        status: obj.status,
        sections: obj.sections,
      };

      // Build URL
      const url = "/api/newsletters/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete newsletters
export const deleteNewsletters = createAsyncThunk(
  "content/newsletters/delete",
  async (
    obj: {
      newsletterIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.newsletterIds },
      };

      const res = await axios.delete("/api/newsletters/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get weblinks
export const getWeblinks = createAsyncThunk(
  "content/weblinks/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/content/weblink/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create weblink
export const createWeblink = createAsyncThunk(
  "content/weblink/post",
  async (
    obj: {
      name: { [key: string]: string };
      description: { [key: string]: string };
      type: string;
      property: string;
      rooms: string[];
      image: { id: any; source: string; data: any } | null;
      url: string;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        description: obj.description,
        type: obj.type,
        property: obj.property,
        image: obj.image,
        url: obj.url,
        rooms: obj.rooms,
        status: obj.status,
      };

      // Build URL
      const url = "/api/content/weblink/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update weblink
export const updateWeblink = createAsyncThunk(
  "content/weblink/put",
  async (
    obj: {
      weblinkId: string;
      name: { [key: string]: string };
      description: { [key: string]: string };
      type: string;
      property: string;
      rooms: string[];
      image: { id: any; source: string; data: any } | null;
      url: string;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.weblinkId,
        name: obj.name,
        description: obj.description,
        type: obj.type,
        property: obj.property,
        image: obj.image,
        url: obj.url,
        rooms: obj.rooms,
        status: obj.status,
      };

      // Build URL
      const url = "/api/content/weblink/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete weblinks
export const deleteWeblinks = createAsyncThunk(
  "content/weblinks/delete",
  async (
    obj: {
      weblinkIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.weblinkIds },
      };

      const res = await axios.delete("/api/content/weblink/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get trivia
export const getTrivia = createAsyncThunk(
  "content/trivia/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/property/trivia/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create trivia
export const createTrivia = createAsyncThunk(
  "content/trivia/post",
  async (
    obj: {
      property: string;
      intent: string;
      status: string;
      response: { [key: string]: string };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        property: obj.property,
        intent: obj.intent,
        status: obj.status,
        response: obj.response,
      };

      // Build URL
      const url = "/api/property/trivia/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update trivia
export const updateTrivia = createAsyncThunk(
  "content/trivia/put",
  async (
    obj: {
      triviaId: string;
      property: string;
      intent: string;
      status: string;
      response: { [key: string]: string };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.triviaId,
        property: obj.property,
        intent: obj.intent,
        status: obj.status,
        response: obj.response,
      };

      // Build URL
      const url = "/api/property/trivia/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete trivia
export const deleteTrivia = createAsyncThunk(
  "content/trivia/delete",
  async (
    obj: {
      triviaIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.triviaIds },
      };

      const res = await axios.delete("/api/property/trivia/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get campaigns
      .addCase(getCampaigns.pending, (state) => {
        state.campaigns.loading = true;
        state.campaigns.waiting = false;
      })
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.campaigns.data = action.payload.data.data;
        state.options.recurring_choices =
          action.payload.data.options.recurring_choices;
        state.options.dynamic_choices =
          action.payload.data.options.dynamic_choices;
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })
      .addCase(getCampaigns.rejected, (state) => {
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })

      // Create pinned campaign
      .addCase(createPinnedCampaign.pending, (state) => {
        state.campaigns.loading = true;
        state.campaigns.waiting = false;
      })
      .addCase(createPinnedCampaign.fulfilled, (state, action) => {
        state.campaigns.data =
          state.campaigns.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.campaigns.data];
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })
      .addCase(createPinnedCampaign.rejected, (state) => {
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })

      // Create rotating campaign
      .addCase(createRotatingCampaign.pending, (state) => {
        state.campaigns.loading = true;
        state.campaigns.waiting = false;
      })
      .addCase(createRotatingCampaign.fulfilled, (state, action) => {
        state.campaigns.data =
          state.campaigns.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.campaigns.data];
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })
      .addCase(createRotatingCampaign.rejected, (state) => {
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })

      // Create template campaign
      .addCase(createTemplateCampaign.pending, (state) => {
        state.campaigns.loading = true;
        state.campaigns.waiting = false;
      })
      .addCase(createTemplateCampaign.fulfilled, (state, action) => {
        state.campaigns.data =
          state.campaigns.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.campaigns.data];
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })
      .addCase(createTemplateCampaign.rejected, (state) => {
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })

      // Delete campaigns
      .addCase(deleteCampaigns.pending, (state) => {
        state.campaigns.loading = true;
        state.campaigns.waiting = false;
      })
      .addCase(deleteCampaigns.fulfilled, (state, action) => {
        const existingData =
          state.campaigns.data === null ? [] : state.campaigns.data;
        const updatedData = existingData.filter(
          (campaign) => !action.payload.data.id.includes(campaign.id),
        );
        state.campaigns.data = updatedData;
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })
      .addCase(deleteCampaigns.rejected, (state) => {
        state.campaigns.loading = false;
        state.campaigns.waiting = false;
      })

      // Get photo albums
      .addCase(getPhotoAlbums.pending, (state) => {
        state.photo_albums.loading = true;
        state.photo_albums.waiting = false;
      })
      .addCase(getPhotoAlbums.fulfilled, (state, action) => {
        state.photo_albums.data = action.payload.data.data;
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })
      .addCase(getPhotoAlbums.rejected, (state) => {
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })

      // Create photo album
      .addCase(createPhotoAlbum.pending, (state) => {
        state.photo_albums.loading = true;
        state.photo_albums.waiting = false;
      })
      .addCase(createPhotoAlbum.fulfilled, (state, action) => {
        state.photo_albums.data =
          state.photo_albums.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.photo_albums.data];
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })
      .addCase(createPhotoAlbum.rejected, (state) => {
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })

      // Update photo album
      .addCase(updatePhotoAlbum.pending, (state) => {
        state.photo_albums.waiting = true;
        state.photo_albums.loading = true;
      })
      .addCase(updatePhotoAlbum.fulfilled, (state, action) => {
        const previousData =
          state.photo_albums.data === null ? [] : state.photo_albums.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const album = previousData[i];
          if (album.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(album);
          }
        }
        state.photo_albums.data = newData;
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })
      .addCase(updatePhotoAlbum.rejected, (state) => {
        state.photo_albums.waiting = false;
        state.photo_albums.loading = false;
      })

      // Delete photo albums
      .addCase(deletePhotoAlbums.pending, (state) => {
        state.photo_albums.loading = true;
        state.photo_albums.waiting = false;
      })
      .addCase(deletePhotoAlbums.fulfilled, (state, action) => {
        const existingData =
          state.photo_albums.data === null ? [] : state.photo_albums.data;
        const updatedData = existingData.filter(
          (album) => !action.payload.data.id.includes(album.id),
        );
        state.photo_albums.data = updatedData;
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })
      .addCase(deletePhotoAlbums.rejected, (state) => {
        state.photo_albums.loading = false;
        state.photo_albums.waiting = false;
      })

      // Get events
      .addCase(getEvents.pending, (state) => {
        state.events.loading = true;
        state.events.waiting = false;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.events.data = action.payload.data.data;
        state.options.events = action.payload.data.options.events;
        state.options.schedule = action.payload.data.options.schedule;
        state.options.recurring = action.payload.data.options.recurring;
        state.options.status = action.payload.data.options.status;
        state.events.loading = false;
        state.events.waiting = false;
      })
      .addCase(getEvents.rejected, (state) => {
        state.events.loading = false;
        state.events.waiting = false;
      })

      // Create event
      .addCase(createEvent.pending, (state) => {
        state.events.loading = true;
        state.events.waiting = false;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.data =
          state.events.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.events.data];
        state.events.loading = false;
        state.events.waiting = false;
      })
      .addCase(createEvent.rejected, (state) => {
        state.events.loading = false;
        state.events.waiting = false;
      })

      // Update event
      .addCase(updateEvent.pending, (state) => {
        state.events.waiting = true;
        state.events.loading = true;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        const previousData =
          state.events.data === null ? [] : state.events.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const event = previousData[i];
          if (event.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(event);
          }
        }
        state.events.data = newData;
        state.events.loading = false;
        state.events.waiting = false;
      })
      .addCase(updateEvent.rejected, (state) => {
        state.events.waiting = false;
        state.events.loading = false;
      })

      // Delete events
      .addCase(deleteEvents.pending, (state) => {
        state.events.loading = true;
        state.events.waiting = false;
      })
      .addCase(deleteEvents.fulfilled, (state, action) => {
        const existingData =
          state.events.data === null ? [] : state.events.data;
        const updatedData = existingData.filter(
          (event) => !action.payload.data.id.includes(event.id),
        );
        state.events.data = updatedData;
        state.events.loading = false;
        state.events.waiting = false;
      })
      .addCase(deleteEvents.rejected, (state) => {
        state.events.loading = false;
        state.events.waiting = false;
      })

      // Get services
      .addCase(getServices.pending, (state) => {
        state.services.loading = true;
        state.services.waiting = false;
      })
      .addCase(getServices.fulfilled, (state, action) => {
        state.services.data = action.payload.data.data;
        state.options.category = action.payload.data.options.category;
        state.options.availability = action.payload.data.options.availability;
        state.services.loading = false;
        state.services.waiting = false;
      })
      .addCase(getServices.rejected, (state) => {
        state.services.loading = false;
        state.services.waiting = false;
      })

      // Create service
      .addCase(createService.pending, (state) => {
        state.services.loading = true;
        state.services.waiting = false;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.services.data =
          state.services.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.services.data];
        state.services.loading = false;
        state.services.waiting = false;
      })
      .addCase(createService.rejected, (state) => {
        state.services.loading = false;
        state.services.waiting = false;
      })

      // Update service
      .addCase(updateService.pending, (state) => {
        state.services.waiting = true;
        state.services.loading = true;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const previousData =
          state.services.data === null ? [] : state.services.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const event = previousData[i];
          if (event.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(event);
          }
        }
        state.services.data = newData;
        state.services.loading = false;
        state.services.waiting = false;
      })
      .addCase(updateService.rejected, (state) => {
        state.services.waiting = false;
        state.services.loading = false;
      })

      // Delete services
      .addCase(deleteServices.pending, (state) => {
        state.services.loading = true;
        state.services.waiting = false;
      })
      .addCase(deleteServices.fulfilled, (state, action) => {
        const existingData =
          state.services.data === null ? [] : state.services.data;
        const updatedData = existingData.filter(
          (service) => !action.payload.data.id.includes(service.id),
        );
        state.services.data = updatedData;
        state.services.loading = false;
        state.services.waiting = false;
      })
      .addCase(deleteServices.rejected, (state) => {
        state.services.loading = false;
        state.services.waiting = false;
      })

      // Get menus
      .addCase(getMenus.pending, (state) => {
        state.menus.loading = true;
        state.menus.waiting = false;
      })
      .addCase(getMenus.fulfilled, (state, action) => {
        state.menus.data = action.payload.data.data;
        state.options.meal_times = action.payload.data.options.meal_times;
        state.options.schedule = action.payload.data.options.schedule;
        state.options.recurring = action.payload.data.options.recurring;
        state.menus.pagination = action.payload.data.pagination;
        state.menus.loading = false;
        state.menus.waiting = false;
      })
      .addCase(getMenus.rejected, (state) => {
        state.menus.loading = false;
        state.menus.waiting = false;
      })

      // Create menu
      .addCase(createMenu.pending, (state) => {
        state.menus.loading = true;
        state.menus.waiting = false;
      })
      .addCase(createMenu.fulfilled, (state, action) => {
        state.menus.data =
          state.menus.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.menus.data];
        state.menus.loading = false;
        state.menus.waiting = false;
      })
      .addCase(createMenu.rejected, (state) => {
        state.menus.loading = false;
        state.menus.waiting = false;
      })

      // Update menu
      .addCase(updateMenu.pending, (state) => {
        state.menus.waiting = true;
        state.menus.loading = true;
      })
      .addCase(updateMenu.fulfilled, (state, action) => {
        const previousData = state.menus.data === null ? [] : state.menus.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const menu = previousData[i];
          if (menu.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(menu);
          }
        }
        state.menus.data = newData;
        state.menus.loading = false;
        state.menus.waiting = false;
      })
      .addCase(updateMenu.rejected, (state) => {
        state.menus.waiting = false;
        state.menus.loading = false;
      })

      // Delete menus
      .addCase(deleteMenus.pending, (state) => {
        state.menus.loading = true;
        state.menus.waiting = false;
      })
      .addCase(deleteMenus.fulfilled, (state, action) => {
        const existingData = state.menus.data === null ? [] : state.menus.data;
        const updatedData = existingData.filter(
          (menu) => !action.payload.data.id.includes(menu.id),
        );
        state.menus.data = updatedData;
        state.menus.loading = false;
        state.menus.waiting = false;
      })
      .addCase(deleteMenus.rejected, (state) => {
        state.menus.loading = false;
        state.menus.waiting = false;
      })

      // Get newsletters
      .addCase(getNewsletters.pending, (state) => {
        state.newsletters.loading = true;
        state.newsletters.waiting = false;
      })
      .addCase(getNewsletters.fulfilled, (state, action) => {
        state.newsletters.data = action.payload.data.data;
        state.options.status = action.payload.data.options.status;
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })
      .addCase(getNewsletters.rejected, (state) => {
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })

      // Create newsletter
      .addCase(createNewsletter.pending, (state) => {
        state.newsletters.loading = true;
        state.newsletters.waiting = false;
      })
      .addCase(createNewsletter.fulfilled, (state, action) => {
        state.newsletters.data =
          state.newsletters.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.newsletters.data];
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })
      .addCase(createNewsletter.rejected, (state) => {
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })

      // Update newsletter
      .addCase(updateNewsletter.pending, (state) => {
        state.newsletters.waiting = true;
        state.newsletters.loading = true;
      })
      .addCase(updateNewsletter.fulfilled, (state, action) => {
        const previousData =
          state.newsletters.data === null ? [] : state.newsletters.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const newsletter = previousData[i];
          if (newsletter.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(newsletter);
          }
        }
        state.newsletters.data = newData;
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })
      .addCase(updateNewsletter.rejected, (state) => {
        state.newsletters.waiting = false;
        state.newsletters.loading = false;
      })

      // Delete newsletters
      .addCase(deleteNewsletters.pending, (state) => {
        state.newsletters.loading = true;
        state.newsletters.waiting = false;
      })
      .addCase(deleteNewsletters.fulfilled, (state, action) => {
        const existingData =
          state.newsletters.data === null ? [] : state.newsletters.data;
        const updatedData = existingData.filter(
          (newsletter) => !action.payload.data.id.includes(newsletter.id),
        );
        state.newsletters.data = updatedData;
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })
      .addCase(deleteNewsletters.rejected, (state) => {
        state.newsletters.loading = false;
        state.newsletters.waiting = false;
      })

      // Get weblinks
      .addCase(getWeblinks.pending, (state) => {
        state.weblinks.loading = true;
        state.weblinks.waiting = false;
      })
      .addCase(getWeblinks.fulfilled, (state, action) => {
        state.weblinks.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })
      .addCase(getWeblinks.rejected, (state) => {
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })

      // Create weblink
      .addCase(createWeblink.pending, (state) => {
        state.weblinks.loading = true;
        state.weblinks.waiting = false;
      })
      .addCase(createWeblink.fulfilled, (state, action) => {
        state.weblinks.data =
          state.weblinks.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.weblinks.data];
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })
      .addCase(createWeblink.rejected, (state) => {
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })

      // Update weblink
      .addCase(updateWeblink.pending, (state) => {
        state.weblinks.waiting = true;
        state.weblinks.loading = true;
      })
      .addCase(updateWeblink.fulfilled, (state, action) => {
        const previousData =
          state.weblinks.data === null ? [] : state.weblinks.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const event = previousData[i];
          if (event.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(event);
          }
        }
        state.weblinks.data = newData;
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })
      .addCase(updateWeblink.rejected, (state) => {
        state.weblinks.waiting = false;
        state.weblinks.loading = false;
      })

      // Delete weblinks
      .addCase(deleteWeblinks.pending, (state) => {
        state.weblinks.loading = true;
        state.weblinks.waiting = false;
      })
      .addCase(deleteWeblinks.fulfilled, (state, action) => {
        const existingData =
          state.weblinks.data === null ? [] : state.weblinks.data;
        const updatedData = existingData.filter(
          (weblink) => !action.payload.data.id.includes(weblink.id),
        );
        state.weblinks.data = updatedData;
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })
      .addCase(deleteWeblinks.rejected, (state) => {
        state.weblinks.loading = false;
        state.weblinks.waiting = false;
      })

      // Get trivia
      .addCase(getTrivia.pending, (state) => {
        state.trivia.loading = true;
        state.trivia.waiting = false;
      })
      .addCase(getTrivia.fulfilled, (state, action) => {
        state.trivia.data = action.payload.data.data;
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })
      .addCase(getTrivia.rejected, (state) => {
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })

      // Create trivia
      .addCase(createTrivia.pending, (state) => {
        state.trivia.loading = true;
        state.trivia.waiting = false;
      })
      .addCase(createTrivia.fulfilled, (state, action) => {
        state.trivia.data =
          state.trivia.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.trivia.data];
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })
      .addCase(createTrivia.rejected, (state) => {
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })

      // Update trivia
      .addCase(updateTrivia.pending, (state) => {
        state.trivia.waiting = true;
        state.trivia.loading = true;
      })
      .addCase(updateTrivia.fulfilled, (state, action) => {
        const previousData =
          state.trivia.data === null ? [] : state.trivia.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const event = previousData[i];
          if (event.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(event);
          }
        }
        state.trivia.data = newData;
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })
      .addCase(updateTrivia.rejected, (state) => {
        state.trivia.waiting = false;
        state.trivia.loading = false;
      })

      // Delete trivia
      .addCase(deleteTrivia.pending, (state) => {
        state.trivia.loading = true;
        state.trivia.waiting = false;
      })
      .addCase(deleteTrivia.fulfilled, (state, action) => {
        const existingData =
          state.trivia.data === null ? [] : state.trivia.data;
        const updatedData = existingData.filter(
          (trv) => !action.payload.data.id.includes(trv.id),
        );
        state.trivia.data = updatedData;
        state.trivia.loading = false;
        state.trivia.waiting = false;
      })
      .addCase(deleteTrivia.rejected, (state) => {
        state.trivia.loading = false;
        state.trivia.waiting = false;
      });
  },
});

export default contentSlice.reducer;
